import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { TenantConfig } from "./types";

interface TenantPatch {
  name?: string;
  type?: string;
  theme?: Partial<TenantConfig["theme"]>;
  profile?: Record<string, unknown>;
  modules?: string[];
}

interface TenantCtxValue {
  tenant: TenantConfig;
  patchTenant: (patch: TenantPatch) => void;
}

const TenantCtx = createContext<TenantCtxValue | null>(null);

export function TenantProvider({ value, children }: { value: TenantConfig; children: ReactNode }) {
  const [tenant, setTenant] = useState<TenantConfig>(value);

  const patchTenant = useCallback((patch: TenantPatch) => {
    setTenant((t) => ({
      ...t,
      ...(patch.name !== undefined ? { name: patch.name } : {}),
      ...(patch.type !== undefined ? { type: patch.type } : {}),
      ...(patch.modules !== undefined ? { modules: patch.modules } : {}),
      theme: patch.theme ? { ...t.theme, ...patch.theme } : t.theme,
      profile: patch.profile ? { ...t.profile, ...patch.profile } : t.profile,
    }));
  }, []);

  const ctx = useMemo(() => ({ tenant, patchTenant }), [tenant, patchTenant]);
  return <TenantCtx.Provider value={ctx}>{children}</TenantCtx.Provider>;
}

export function useTenant(): TenantConfig {
  const ctx = useContext(TenantCtx);
  if (!ctx) throw new Error("useTenant must be used within <TenantProvider>");
  return ctx.tenant;
}

export function useTenantPatch(): (patch: TenantPatch) => void {
  const ctx = useContext(TenantCtx);
  if (!ctx) throw new Error("useTenantPatch must be used within <TenantProvider>");
  return ctx.patchTenant;
}
